import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { ArrowLeft, Save } from 'lucide-react';
import { eventService } from '../../services';
import type { Event } from '../../types';

const EditActionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [action, setAction] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    event_note: '',
    event_state: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      fetchAction(parseInt(id));
    }
  }, [id]);

  const fetchAction = async (actionId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await eventService.getEventById(actionId);
      setAction(response);
      setFormData({
        event_note: response.event_note || '',
        event_state: response.event_state || 'active',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch action');
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (formData.event_note && formData.event_note.length > 2000) {
      errors.event_note = 'Notes cannot exceed 2000 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !id) return;

    try {
      setIsSaving(true);
      setError(null);
      await eventService.updateEvent(parseInt(id), formData);
      setSuccessMessage('Action updated successfully');
      setTimeout(() => {
        navigate(`/actions/${id}/view`);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update action');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <Loading size="lg" text="Loading action..." />
        </div>
      </MainLayout>
    );
  }

  if (error && !action) {
    return (
      <MainLayout>
        <Alert
          variant="error"
          title="Error"
          message={error}
        />
        <Button
          variant="outline"
          leftIcon={<ArrowLeft size={18} />}
          onClick={() => navigate('/actions')}
          className="mt-4"
        >
          Back to Actions
        </Button>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/actions/${id}/view`)}
              leftIcon={<ArrowLeft size={18} />}
            >
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Edit Action</h1>
              <p className="text-sm text-text-muted mt-1">Update action information</p>
            </div>
          </div>
        </div>

        {error && <Alert variant="error" title="Error" message={error} />}
        {successMessage && <Alert variant="success" title="Success" message={successMessage} />}

        {/* Read-only Information */}
        <Card>
          <h2 className="text-lg font-semibold text-text-primary mb-4">Action Information (Read-only)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Action ID
              </label>
              <div className="text-text-primary">#{action?.event_id}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Action Type
              </label>
              <div className="text-text-primary">{action?.event_tag?.replace(/_/g, ' ') || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Location
              </label>
              <div className="text-text-primary">
                {action?.location_name || `Location #${action?.event_location}`}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Created Date
              </label>
              <div className="text-text-primary">
                {action?.created_date ? new Date(action.created_date).toLocaleString() : 'N/A'}
              </div>
            </div>
          </div>
        </Card>

        {/* Editable Form */}
        <Card>
          <h2 className="text-lg font-semibold text-text-primary mb-4">Editable Information</h2>
          <div className="space-y-4">
            <Select
              label="State"
              value={formData.event_state}
              onChange={(e) => setFormData({ ...formData, event_state: e.target.value })}
              options={[
                { label: 'Active', value: 'active' },
                { label: 'Resolved', value: 'resolved' },
                { label: 'Dismissed', value: 'dismissed' },
              ]}
              fullWidth
            />

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Notes
              </label>
              <textarea
                value={formData.event_note}
                onChange={(e) => setFormData({ ...formData, event_note: e.target.value })}
                placeholder="Add or update action notes..."
                className="w-full px-3 py-2 border border-surface-secondary rounded-lg bg-background-secondary text-text-primary min-h-[120px]"
                rows={6}
              />
              {formErrors.event_note && (
                <p className="text-sm text-status-danger mt-1">{formErrors.event_note}</p>
              )}
              <p className="text-xs text-text-muted mt-1">
                {formData.event_note.length} / 2000 characters
              </p>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/actions/${id}/view`)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            leftIcon={<Save size={18} />}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditActionPage;
